var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import { instructionDataRecoil, isLiveProcWarningModalOpenRecoil, evaluationConfigRecoil, } from "../../atoms/global-state";
import { useApplicationContext } from "../../context/application-context";
// No payload building here; caller provides submit implementation
import { InterviewEventEnum } from "../../submodules/Assessment-Platform-1.0-Dtos-FE/src/enums/interview/interviewEventEnum.enum";
import { UserResultSubmitReason } from "../../submodules/Assessment-Platform-1.0-Dtos-FE/src/enums/userExamStatus.enum";
import { NetworkStatus, useNetworkStatus } from "../../utils/network-status";
import { MEETING_ROUTES } from "../../constants/routes";
export function useScreenDiscipline(options) {
    const { isEnabled = true, isPractice = false, iframeRef, maxWarnings = 3, countdowns = [15000, 15000, 15000], submitAssessment, } = options || {};
    const navigate = useNavigate();
    const networkStatus = useNetworkStatus();
    const instructionData = useRecoilValue(instructionDataRecoil);
    const evalConfig = useRecoilValue(evaluationConfigRecoil);
    const isWarningDrawerOpen = useRecoilValue(isLiveProcWarningModalOpenRecoil);
    const { user, tenantSettings } = useApplicationContext();
    // interview meta handled by caller's submit function
    // State
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [exitScreenCounter, setExitScreenCounter] = useState(0);
    const [fullscreenWarningOpen, setFullscreenWarningOpen] = useState(false);
    const [fullscreenDeadline, setFullscreenDeadline] = useState(null);
    const [networkWarningOpen, setNetworkWarningOpen] = useState(false);
    const [networkDeadline, setNetworkDeadline] = useState(null);
    const [proctorWarningOpen, setProctorWarningOpen] = useState(false);
    const [proctorDeadline, setProctorDeadline] = useState(null);
    const [endOverlayOpen, setEndOverlayOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const countdownIndex = Math.min(exitScreenCounter, countdowns.length - 1);
    // Remaining warnings before assessment auto-ends
    const warningsLeft = Math.max(0, maxWarnings - exitScreenCounter);
    // Helpers
    const now = () => new Date().getTime();
    const everEnteredFullscreenRef = useRef(false);
    const violationArmedRef = useRef(false);
    const armTimeoutRef = useRef(null);
    const effectiveEnabled = isEnabled;
    const armViolationsSoon = useCallback(() => {
        // Disarm first to avoid overlaps
        violationArmedRef.current = false;
        if (armTimeoutRef.current) {
            window.clearTimeout(armTimeoutRef.current);
            armTimeoutRef.current = null;
        }
        // Arm after a small grace period to avoid immediate false triggers on entry
        armTimeoutRef.current = window.setTimeout(() => {
            violationArmedRef.current = true;
        }, 800);
    }, []);
    const requestFullscreen = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            if (document.fullscreenElement) {
                setIsFullscreen(true);
                return;
            }
            const el = document.documentElement;
            const fn = el.requestFullscreen ||
                el.webkitRequestFullscreen ||
                el.mozRequestFullScreen ||
                el.msRequestFullscreen;
            if (fn) {
                yield fn.call(el);
                setIsFullscreen(true);
                everEnteredFullscreenRef.current = true;
                armViolationsSoon();
            }
            else {
                // Not supported – keep UI warnings but don't hard-fail
                setIsFullscreen(false);
            }
        }
        catch (_a) {
            setIsFullscreen(false);
        }
    }), [armViolationsSoon]);
    const exitFullscreen = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            const active = !!document.fullscreenElement;
            if (!active) {
                setIsFullscreen(false);
                return;
            }
            const fn = document.exitFullscreen ||
                document.webkitExitFullscreen ||
                document.mozCancelFullScreen ||
                document.msExitFullscreen;
            if (fn) {
                yield fn.call(document);
            }
        }
        catch (_b) {
            // suppress errors like 'Document not active'
        }
        finally {
            setIsFullscreen(false);
        }
    }), []);
    const calculateNextFullscreenDeadline = useCallback(() => {
        const ms = countdowns[countdownIndex];
        setFullscreenDeadline(now() + ms);
    }, [countdownIndex, countdowns]);
    const endAndOverlay = useCallback(() => {
        setEndOverlayOpen(true);
    }, []);
    const autoSubmitCheating = useCallback((source) => __awaiter(this, void 0, void 0, function* () {
        if (isPractice)
            return;
        switch (source) {
            case "EXIT_FULLSCREEN":
            case "TAB_SWITCH":
            case "MOUSE_LEAVE":
                yield submitAssessment("AUTO_END_TEST_CHEATING", UserResultSubmitReason.MALPRACTICE_COUNTER_EXCEEDED);
                break;
            case "EXTERNAL_DEVICE":
                yield submitAssessment("AUTO_END_TEST_CHEATING", UserResultSubmitReason.PROCTORING_TEST_FAILED);
                break;
            case "NETWORK_TIMEOUT":
            case "DURATION_TIMEOUT":
                yield submitAssessment(InterviewEventEnum.AUTO_END_TEST, UserResultSubmitReason.TIMEOUT);
                break;
        }
        endAndOverlay();
    }), [submitAssessment, isPractice, endAndOverlay]);
    const handleFullscreenViolation = useCallback(() => {
        if (!isEnabled || isPractice)
            return;
        // Increment counter for tracking purposes (no limit enforced)
        setExitScreenCounter((prev) => prev + 1);
        // Always show warning modal with 15-second countdown
        calculateNextFullscreenDeadline();
        setFullscreenWarningOpen(true);
    }, [isEnabled, isPractice, calculateNextFullscreenDeadline]);
    const onFullscreenChange = useCallback(() => {
        const active = !!document.fullscreenElement;
        setIsFullscreen(active);
        if (active) {
            everEnteredFullscreenRef.current = true;
            setFullscreenWarningOpen(false);
            setFullscreenDeadline(null);
            armViolationsSoon();
        }
        else {
            // Only treat as violation if we have entered fullscreen at least once
            if (everEnteredFullscreenRef.current) {
                handleFullscreenViolation();
            }
            violationArmedRef.current = false;
        }
    }, [handleFullscreenViolation, armViolationsSoon]);
    // Network monitoring
    useEffect(() => {
        if (!effectiveEnabled)
            return;
        if (networkStatus.status === NetworkStatus.ONLINE) {
            setNetworkWarningOpen(false);
            setNetworkDeadline(null);
            requestFullscreen();
        }
        else {
            setNetworkWarningOpen(true);
            setNetworkDeadline(now() + 180000); // 3 minutes like existing
            if (document.fullscreenElement) {
                exitFullscreen();
            }
        }
    }, [
        effectiveEnabled,
        networkStatus.status,
        exitFullscreen,
        requestFullscreen,
    ]);
    // Base listeners – blur/visibility/mouseleave/resize
    useEffect(() => {
        if (!effectiveEnabled)
            return;
        const onBlur = () => {
            // Ignore when mouse over iframe
            if (iframeRef === null || iframeRef === void 0 ? void 0 : iframeRef.current) {
                const rect = iframeRef.current.getBoundingClientRect();
                const x = window.mouseX;
                const y = window.mouseY;
                if (typeof x === "number" &&
                    typeof y === "number" &&
                    x >= rect.left &&
                    x <= rect.right &&
                    y >= rect.top &&
                    y <= rect.bottom) {
                    return;
                }
            }
            if (document.fullscreenElement && violationArmedRef.current) {
                exitFullscreen();
            }
        };
        const onVisibility = () => {
            if (document.visibilityState === "hidden" &&
                document.fullscreenElement &&
                violationArmedRef.current) {
                exitFullscreen();
            }
        };
        const onMouseMove = (e) => {
            window.mouseX = e.clientX;
            window.mouseY = e.clientY;
        };
        const onMouseLeave = (e) => {
            if (e.clientY <= 0 ||
                e.clientX <= 0 ||
                e.clientX >= window.innerWidth ||
                e.clientY >= window.innerHeight) {
                if (document.fullscreenElement && violationArmedRef.current) {
                    exitFullscreen();
                }
            }
        };
        const onResize = () => {
            if (window.outerHeight - window.innerHeight > 100) {
                if (isFullscreen) {
                    handleFullscreenViolation();
                }
            }
        };
        document.addEventListener("fullscreenchange", onFullscreenChange);
        window.addEventListener("blur", onBlur);
        document.addEventListener("visibilitychange", onVisibility);
        document.addEventListener("mouseleave", onMouseLeave);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("resize", onResize);
        return () => {
            document.removeEventListener("fullscreenchange", onFullscreenChange);
            window.removeEventListener("blur", onBlur);
            document.removeEventListener("visibilitychange", onVisibility);
            document.removeEventListener("mouseleave", onMouseLeave);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("resize", onResize);
        };
    }, [
        effectiveEnabled,
        iframeRef,
        isFullscreen,
        exitFullscreen,
        onFullscreenChange,
        handleFullscreenViolation,
    ]);
    // Auto-submit engine handled by provided submit function
    // Fullscreen deadline watcher - resets countdown when expired (no auto-end)
    useEffect(() => {
        if (!fullscreenWarningOpen || !fullscreenDeadline)
            return;
        const id = setInterval(() => {
            if (now() >= fullscreenDeadline) {
                clearInterval(id);
                // Reset the countdown to keep showing the warning
                // User must click button to re-enter fullscreen
                calculateNextFullscreenDeadline();
            }
        }, 250);
        return () => clearInterval(id);
    }, [
        fullscreenWarningOpen,
        fullscreenDeadline,
        calculateNextFullscreenDeadline,
    ]);
    // Network deadline watcher
    useEffect(() => {
        if (!networkWarningOpen || !networkDeadline)
            return;
        const id = setInterval(() => {
            if (now() >= networkDeadline) {
                clearInterval(id);
                setNetworkWarningOpen(false);
                autoSubmitCheating("NETWORK_TIMEOUT");
            }
        }, 500);
        return () => clearInterval(id);
    }, [networkWarningOpen, networkDeadline, autoSubmitCheating]);
    // Proctoring external device deadline watcher (2 minutes like existing)
    useEffect(() => {
        if (!proctorWarningOpen || !proctorDeadline)
            return;
        const id = setInterval(() => {
            if (now() >= proctorDeadline) {
                clearInterval(id);
                setProctorWarningOpen(false);
                autoSubmitCheating("EXTERNAL_DEVICE");
            }
        }, 500);
        return () => clearInterval(id);
    }, [proctorWarningOpen, proctorDeadline, autoSubmitCheating]);
    // Initialize – try to enter fullscreen if enabled and online
    useEffect(() => {
        if (!effectiveEnabled)
            return;
        if (networkStatus.status === NetworkStatus.ONLINE) {
            requestFullscreen();
        }
    }, [effectiveEnabled, networkStatus.status, requestFullscreen]);
    // Public actions
    const acknowledgeFullscreenWarning = useCallback(() => {
        setFullscreenWarningOpen(false);
        requestFullscreen();
    }, [requestFullscreen]);
    const triggerExternalDeviceViolation = useCallback(() => {
        if (!effectiveEnabled || isPractice)
            return;
        setProctorWarningOpen(true);
        setProctorDeadline(now() + 120000);
    }, [effectiveEnabled, isPractice]);
    const endManually = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isPractice) {
            navigate(MEETING_ROUTES.FINISH_PAGE);
            return;
        }
        yield submitAssessment(InterviewEventEnum.END_TEST, UserResultSubmitReason.NORMAL);
        endAndOverlay();
    }), [isPractice, navigate, submitAssessment, endAndOverlay]);
    return {
        state: {
            isOnline: networkStatus.status === NetworkStatus.ONLINE,
            isFullscreen,
            warningsLeft,
            fullscreenWarningOpen,
            fullscreenDeadline,
            networkWarningOpen,
            networkDeadline,
            proctorWarningOpen,
            proctorDeadline,
            endOverlayOpen,
            isSubmitting,
        },
        actions: {
            enterFullscreen: requestFullscreen,
            exitFullscreen,
            acknowledgeFullscreenWarning,
            triggerExternalDeviceViolation,
            endManually,
        },
        uiFlags: {
            isWarningDrawerOpen,
            tenantSettings,
        },
    };
}
